import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchGuaranteesMainDeals } from '~/store/guarantees/thunks/applicationThunks.ts';
import { GuaranteesFetchStatus } from '~/store/guarantees/types.ts';
import { buildGuaranteesMainDealsRequestKey } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';

type TUseInitialGuaranteesDictionariesParams = {
  contragentId: string;
  isCurrenciesError: boolean;
  isCurrenciesLoading: boolean;
  mainDealsRequestKey: string | null;
  mainDealsStatus: GuaranteesFetchStatus;
  refetchCurrencies: () => Promise<{ error: unknown | null }>;
};

export const useInitialGuaranteesDictionaries = ({
  contragentId,
  isCurrenciesError,
  isCurrenciesLoading,
  mainDealsRequestKey: storedMainDealsRequestKey,
  mainDealsStatus,
  refetchCurrencies,
}: TUseInitialGuaranteesDictionariesParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isRetrying, setIsRetrying] = useState(false);

  const loadMainDeals = useCallback(
    () => dispatch(fetchGuaranteesMainDeals({ contragentId, page: 0, size: 20 })),
    [contragentId, dispatch]
  );

  useEffect(() => {
    if (contragentId) {
      loadMainDeals();
    }
  }, [contragentId, loadMainDeals]);

  const hasCurrentMainDealsRequest =
    storedMainDealsRequestKey ===
    buildGuaranteesMainDealsRequestKey({ contragentId, page: 0, size: 20 });
  const isMainDealsError =
    hasCurrentMainDealsRequest && mainDealsStatus === GuaranteesFetchStatus.Error;
  const hasError = isRetrying || isCurrenciesError || isMainDealsError;
  const isMainDealsLoading =
    !hasCurrentMainDealsRequest ||
    mainDealsStatus === GuaranteesFetchStatus.Idle ||
    mainDealsStatus === GuaranteesFetchStatus.Loading;
  const isLoading =
    !hasError && (isMainDealsLoading || isCurrenciesLoading);

  const retry = useCallback(async () => {
    if (!contragentId) {
      return;
    }

    setIsRetrying(true);
    await Promise.allSettled([loadMainDeals().unwrap(), refetchCurrencies()]);
    setIsRetrying(false);
  }, [contragentId, loadMainDeals, refetchCurrencies]);

  return {
    hasInitialDictionariesError: hasError,
    isInitialDictionariesLoading: isLoading,
    isRetryingInitialDictionaries: isRetrying,
    retryInitialDictionaries: retry,
  };
};
